import { useEffect, useState } from 'react';
import { Notification } from '../types';
import { notificationStore } from '../services/store';

function timeAgo(isoStr: string): string {
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins  = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days  = Math.floor(diff / 86_400_000);
  if (days > 0)  return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins > 0)  return `${mins}m ago`;
  return 'Just now';
}

const TYPE_ICON: Record<string, string> = {
  ExpirationAlert:           '⚠️',
  ContractPendingApproval:   '📋',
  BillingPendingApproval:    '💰',
};

const URGENCY_COLOR = (n: Notification): [string, string] => {
  if (n.type !== 'ExpirationAlert' || n.daysUntil == null) return ['#E6F1FB', '#185FA5'];
  if (n.daysUntil <= 30) return ['#FCEBEB', '#A32D2D'];
  if (n.daysUntil <= 60) return ['#FAEEDA', '#854F0B'];
  return ['#FAEEDA', '#854F0B'];
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const load = () => setNotifications(notificationStore.list());
  useEffect(() => { load(); }, []);

  const markRead = (id: string) => {
    notificationStore.markRead(id);
    load();
  };

  const markAllRead = () => {
    notificationStore.markAllRead();
    load();
  };

  const unread = notifications.filter(n => !n.read).length;

  return (
    <div className="p-8">
      <div className="flex justify-between items-start mb-5">
        <div>
          <h1 className="text-[22px] font-semibold text-gray-900 mb-0.5">Notifications</h1>
          <p className="text-sm text-gray-500">{unread} unread · {notifications.length} total</p>
        </div>
        {unread > 0 && (
          <button
            onClick={markAllRead}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 cursor-pointer hover:bg-gray-50"
          >
            Mark all read
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-4xl mb-3">🔔</div>
          <div className="text-sm">No notifications yet. Contract expiration alerts will appear here.</div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {notifications.map(n => {
            const [bg, color] = URGENCY_COLOR(n);
            return (
              <div
                key={n.id}
                className={`bg-white rounded-xl shadow-sm px-4 py-3 flex items-start gap-3 ${!n.read ? 'border-l-4' : 'border border-gray-100'}`}
                style={!n.read ? { borderLeftColor: color } : {}}
              >
                <span className="text-lg mt-0.5">{TYPE_ICON[n.type] ?? '🔔'}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    {n.daysUntil != null && (
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ background: bg, color }}>
                        {n.daysUntil} days
                      </span>
                    )}
                    {!n.read && (
                      <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
                    )}
                  </div>
                  <p className="text-sm text-gray-800 mb-1">{n.message}</p>
                  <div className="flex items-center gap-3 text-[11px] text-gray-400 flex-wrap">
                    <span>{timeAgo(n.createdAt)}</span>
                    {n.mailtoHref && (
                      <a
                        href={n.mailtoHref}
                        className="text-blue-600 hover:underline font-medium"
                      >
                        📧 Open Outlook reminder draft →
                      </a>
                    )}
                    {!n.read && (
                      <button
                        onClick={() => markRead(n.id)}
                        className="text-gray-400 hover:text-gray-600 cursor-pointer border-0 bg-transparent p-0"
                      >
                        Mark read
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
